import React, { useEffect, useMemo } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
  Paper,
  Divider,
  Grid,
  FormControl,
  FormHelperText,
  InputLabel,
  Stack,
  Collapse,
  Alert,
  IconButton,
} from "@mui/material";
import { TextareaAutosize } from "@mui/base/TextareaAutosize";
import { useSelector } from "react-redux";
import { styled } from "@mui/material/styles";

// Styled components for better UI
const FormSection = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[1],
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  marginBottom: theme.spacing(2),
  fontWeight: 500,
}));

// Constants for dropdown options - defined outside component to prevent recreation
const CALL_STATUS = [
  "Completed",
  "No answer",
  "Call done",
  "Busy",
  "No network",
  "Invalid",
  "Incoming not available",
  "Not in the portal",
  "Miscarriage",
  "Call Incomplete",
  "Deceased Child",
  "Switched off",
  "Wrong number",
  "Declined",
  "Out of service",
  "Language Barrier",
  "Deceased Mother",
  "Deceased (Mother and Child)",
  "Duplicate Mother Id",
  "Not Pregnant",
  "Child Above 2 Years",
];

const CONNECTED_STATUS = [
  "Completed",
  "Call done",
  "Not in the portal",
  "Miscarriage",
  "Call Incomplete",
  "Deceased Child",
  "Language Barrier",
  "Deceased Mother",
  "Deceased (Mother and Child)",
  "Duplicate Mother Id",
  "Not Pregnant",
  "Child Above 2 Years",
];

const NOT_CONNECTED_STATUS = [
  "No answer",
  "Busy",
  "No network",
  "Invalid",
  "Incoming not available",
  "Not in the portal",
  "Switched off",
  "Wrong number",
  "Declined",
  "Out of service",
  "Duplicate Mother Id",
];

const PREGNANCY_MONTH = [
  "Delivered",
  "One",
  "Two",
  "Three",
  "Four",
  "Five",
  "Six",
  "Seven",
  "Eight",
  "Nine",
  "Ten",
];

const TRIMESTER_COUNT = ["Delivered", "One", "Two", "Three", "Four"];

const NOT_SCORING_REASONS = [
  "Beneficiary did not understand the rating system after explanation",
  "Beneficiary does not want to give a rating",
  "Beneficiary disconnects the call before explanation of rating",
  "Beneficiary disconnects the call after explanation of rating",
  "Call done with ASHA/ ANM/ AW",
  "Cannot explain in Hindi",
  "Deceased (Mother and Child)",
  "Deceased Child",
  "Deceased Mother",
  "Currently Hospitalized",
  "Miscarriage",
  "Not pregnant",
];

// Define validation schema with Yup
const createValidationSchema = (connectivityStatus, callStatus, callScore) => {
  return yup.object().shape({
    connectivityStatus: yup.string().required("Connectivity status is required"),
    callStatus: yup.string().required("Call status is required"),
    alternateNumber: yup.string().test({
      name: "is-valid-phone",
      skipAbsent: true,
      test(value, ctx) {
        if (!value) return true;
        const phoneRegex = /^\d{10}$/;
        return phoneRegex.test(value) || ctx.createError({ message: "Please enter a valid 10-digit phone number" });
      },
    }),
    callerType: yup.string().when(["connectivityStatus", "callStatus"], {
      is: (connectivityStatus, callStatus) =>
        connectivityStatus === "Connected" && callStatus !== "Language Barrier",
      then: () => yup.string().required("Caller type is required"),
      otherwise: () => yup.string(),
    }),
    pregnancyStatus: yup.string().when(["connectivityStatus", "callStatus"], {
      is: (connectivityStatus, callStatus) =>
        connectivityStatus === "Connected" && callStatus !== "Language Barrier",
      then: () => yup.string().required("Pregnancy status is required"),
      otherwise: () => yup.string(),
    }),
    ICDSSchemeStatus: yup.string().when(["connectivityStatus", "callStatus"], {
      is: (connectivityStatus, callStatus) =>
        connectivityStatus === "Connected" && callStatus !== "Language Barrier",
      then: () => yup.string().required("ICDS scheme status is required"),
      otherwise: () => yup.string(),
    }),
    notScoringReason: yup.string().when("callScore", {
      is: "Nil",
      then: () => yup.string().required("Please select a reason for not scoring"),
      otherwise: () => yup.string(),
    }),
    notes: yup.string(),
  });
};

/**
 * Enhanced ECD Report Form Component
 *
 * @param {Object} props - Component props
 * @param {Object} props.data - Call data
 * @param {Object} props.ecd - ECD data
 * @param {Function} props.onSubmit - Form submission handler
 * @param {Function} props.onChange - Form change handler
 * @param {string} props.currentStatus - Current call status
 */
const EcdReportForm2 = ({
  data = {},
  ecd = {},
  onSubmit = () => { },
  onChange = () => { },
  currentStatus = null,
}) => {
  // Get user data from Redux store
  const {
    roles,
    user,
    users,
    Designations,
    Departments,
    Languages,
    Districts,
    TaskTypes,
    Campaigns,
    Query,
    SubQuery,
    map,
  } = useSelector((state) => state.user || {});

  // Create form with react-hook-form and yup validation
  const {
    control,
    handleSubmit: handleFormSubmit,
    watch,
    setValue,
    formState: { errors, isValid, isDirty, touchedFields },
    reset,
    trigger,
  } = useForm({
    resolver: yupResolver(createValidationSchema()),
    mode: "onTouched",
    reValidateMode: "onChange",
    defaultValues: {
      connectivityStatus: "",
      callStatus: null,
      alternateNumber: "",
      callerType: "",
      pregnancyStatus: "",
      ICDSSchemeStatus: "",
      callScore: "",
      notScoringReason: "",
      notes: "",
    },
  });

  // Watch form values for conditional logic
  const connectivityStatus = watch("connectivityStatus");
  const callStatus = watch("callStatus");
  const callScore = watch("callScore");

  // Memoized call status options based on connectivity status
  const callStatusOptions = useMemo(() => {
    if (connectivityStatus === "Connected") {
      return CONNECTED_STATUS;
    } else if (connectivityStatus === "Not Connected") {
      return NOT_CONNECTED_STATUS;
    }
    return CALL_STATUS;
  }, [connectivityStatus]);

  // Update validation schema when form values change
  useEffect(() => {
    // This will update the resolver with the new schema based on current values
    const newSchema = createValidationSchema(connectivityStatus, callStatus, callScore);
    // We can't directly update the resolver, but we can trigger validation
    trigger();
  }, [connectivityStatus, callStatus, callScore, trigger]);

  // Effect to update form data when currentStatus changes
  useEffect(() => {
    if (currentStatus) {
      setValue("callStatus", currentStatus);
    }
  }, [currentStatus, setValue]);

  // Effect to update form data when data changes
  useEffect(() => {
    if (data && data.status) {
      setValue("callStatus", null);
    }
  }, [data?.status, setValue]);

  // Effect to reset form data when ecd changes
  useEffect(() => {
    const ecdStr = JSON.stringify(ecd);
    if (ecdStr !== '{}') {
      reset();
    }
  }, [ecd, reset]);

  // Handle special field interactions
  const handleConnectivityChange = (event, onChange) => {
    const value = event.target.value;
    onChange(event);

    if (value === "Connected") {
      // Reset call status when connectivity status changes
      setValue("callStatus", "");
    }
  };

  const handleCallStatusChange = (event, onChange) => {
    const value = event.target.value;
    onChange(event);

    if (connectivityStatus === "Connected" &&
      (value === "Not in the portal" || value === "Call Incomplete")) {
      setValue("cluster", "NA");
      setValue("callerType", "NA");
      setValue("pregnancyStatus", "NA");
      setValue("ICDSSchemeStatus", "NA");
    } else if (value === "Completed") {
      setValue("cycleCompleted", true);
    }
  };

  const handleCallScoreChange = (event, onChange) => {
    const value = event.target.value;
    onChange(event);

    if (value === "Nil") {
      setValue("notScoringReason", null);
    }
  };

  // Handle form submission
  const onSubmitForm = (data) => {
    // Ensure connectivity status and call status are filled
    if (!data.connectivityStatus || !data.callStatus) {
      // Trigger validation to show errors
      trigger(["connectivityStatus", "callStatus"]);
      return;
    }

    // If we reach here, the form is valid
    onSubmit(data);
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box component="form" onSubmit={handleFormSubmit(onSubmitForm)} noValidate>
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="h5" sx={{ color: "#375674", fontWeight: 500 }}>
              ECD Form
            </Typography>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              startIcon={Object.keys(errors).length > 0 ? <span role="img" aria-label="warning">⚠️</span> : null}
            >
              {Object.keys(errors).length > 0 ? "Save Report (Fix Errors)" : "Save Report"}
            </Button>
          </Box>

          <Alert severity="info" sx={{ mb: 2 }}>
            Connectivity Status and Call Status are required fields for form submission.
          </Alert>

          {Object.keys(errors).length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Please fix the highlighted errors before submitting the form.
            </Alert>
          )}
        </Box>

        {/* Basic Call Information Section */}
        <FormSection>
          <SectionTitle variant="h6">Call Information <Typography component="span" color="error" sx={{ fontSize: '0.8rem', ml: 1 }}>(Required)</Typography></SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Controller
                name="connectivityStatus"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    select
                    label="Connectivity Status"
                    error={!!error}
                    helperText={error?.message}
                    margin="dense"
                    size="small"
                    fullWidth
                    required
                    onChange={(e) => handleConnectivityChange(e, field.onChange)}
                    InputLabelProps={{ shrink: true }}
                  >
                    <MenuItem value="Connected">Connected</MenuItem>
                    <MenuItem value="Not Connected">Not Connected</MenuItem>
                  </TextField>
                )}
              />
            </Grid>

            {connectivityStatus && (
              <Grid item xs={12} md={4}>
                <Controller
                  name="callStatus"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      select
                      label="Call Status"
                      error={!!error}
                      helperText={error?.message}
                      margin="dense"
                      size="small"
                      fullWidth
                      required
                      onChange={(e) => handleCallStatusChange(e, field.onChange)}
                      InputLabelProps={{ shrink: true }}
                    >
                      {callStatusOptions.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Grid>
            )}

            {connectivityStatus === "Connected" && (
              <Grid item xs={12} md={4}>
                <Controller
                  name="alternateNumber"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      label="Alternate Number"
                      error={!!error}
                      helperText={error?.message}
                      margin="dense"
                      size="small"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                />
              </Grid>
            )}
          </Grid>
        </FormSection>

        {/* Caller Information Section - Only show if connected */}
        {connectivityStatus === "Connected" && callStatus !== "Language Barrier" && (
          <FormSection>
            <SectionTitle variant="h6">Caller Information</SectionTitle>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Controller
                  name="callerType"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      select
                      label="Caller Type"
                      error={!!error}
                      helperText={error?.message}
                      margin="dense"
                      size="small"
                      fullWidth
                      required
                      InputLabelProps={{ shrink: true }}
                    >
                      {["ASHA", "Mother", "Other", "NA"].map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Controller
                  name="pregnancyStatus"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      select
                      label="Pregnancy Status"
                      error={!!error}
                      helperText={error?.message}
                      margin="dense"
                      size="small"
                      fullWidth
                      required
                      InputLabelProps={{ shrink: true }}
                    >
                      {["Currently Expecting", "Delivered", "NA"].map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Controller
                  name="ICDSSchemeStatus"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      select
                      label="ICDS Scheme Status"
                      error={!!error}
                      helperText={error?.message}
                      margin="dense"
                      size="small"
                      fullWidth
                      required
                      InputLabelProps={{ shrink: true }}
                    >
                      {[
                        "Received only eggs",
                        "Received other foods (excluding eggs)",
                        "Received both eggs and other foods",
                        "Did not received anything",
                        "No ICDS centre available in the area",
                        "Not yet registered under the ICDS Scheme",
                        "No information available",
                        "Not availed",
                        "Not satisfactory food quality",
                        "NA",
                      ].map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Grid>
            </Grid>
          </FormSection>
        )}

        {/* Call Quality Section - Only show if connected */}
        {connectivityStatus === "Connected" && callStatus !== "Language Barrier" && (
          <FormSection>
            <SectionTitle variant="h6">Call Quality</SectionTitle>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Controller
                  name="callScore"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      select
                      label="Call Score"
                      error={!!error}
                      helperText={error?.message}
                      margin="dense"
                      size="small"
                      fullWidth
                      onChange={(e) => handleCallScoreChange(e, field.onChange)}
                      InputLabelProps={{ shrink: true }}
                    >
                      {["Nil", 1, 2, 3, 4, 5].map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Grid>

              {(callScore === "Nil" || !callScore) && (
                <Grid item xs={12} md={8}>
                  <Controller
                    name="notScoringReason"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        select
                        label="Not Scoring Reason"
                        error={!!error}
                        helperText={error?.message}
                        margin="dense"
                        size="small"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                      >
                        {NOT_SCORING_REASONS.map((option) => (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </Grid>
              )}
            </Grid>
          </FormSection>
        )}

        {/* Additional Notes Section - Only show if connected */}
        {connectivityStatus === "Connected" && callStatus !== "Language Barrier" && (
          <FormSection>
            <SectionTitle variant="h6">Additional Notes</SectionTitle>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Controller
                  name="notes"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <Box sx={{ position: 'relative' }}>
                      <TextareaAutosize
                        {...field}
                        className="form-control"
                        minRows={5}
                        placeholder="Enter any additional notes or observations here..."
                        style={{
                          width: '100%',
                          padding: '8px',
                          borderColor: error ? '#d32f2f' : undefined,
                        }}
                      />
                      {error && (
                        <FormHelperText error>{error.message}</FormHelperText>
                      )}
                    </Box>
                  )}
                />
              </Grid>
            </Grid>
          </FormSection>
        )}
      </Box>
    </Box>
  );
};

export default EcdReportForm2;
